
import React, { useEffect, useState } from 'react';
import { PARTNER_TYPES } from '../../config';
import { useHistory } from 'react-router-dom';
import MoonLoader from 'react-spinners/MoonLoader';
import EditClinicAssociations from '../clinics/EditClinicAssociations';
import useUsers from '../../store/users/usersContext';
import { COLORS } from '../../config';
import { COUNTRIES } from '../../helpers/selectOptions';
import FieldWithEdit from '../../components/FieldItems/FieldWithEdit';
import EditIcon from '../../components/Icons/outline/EditIcon';
import ProfilePicture from '../../components/App/ProfilePicture';
import PrimaryButton from '../../components/Buttons/PrimaryButton';
import FieldWithSelectEdit from '../../components/FieldItems/FieldWithSelectEdit';
import ClosedLockIcon from '../../components/Icons/solid/ClosedLockIcon';
import useClinics from '../../store/clinics/clinicsContext';
import RedButton from '../../components/Buttons/RedButton';
import GreenButton from '../../components/Buttons/GreenButton';
import DeleteUserModal from '../modals/DeleteUserModal';
import dayjs from 'dayjs';

function ViewPartnerDetails({
  partnerId : id
}) {

  const history = useHistory();
  
  const {
    getUserDetails,
    updateUserById,
    getPartnerInfo,
    updatePartnerInfo,
    deleteUser,
    activateUser,
    forceDeleteUser,
  } = useUsers();

  const [isLoading, setIsLoading] = React.useState(true);
  const { clinicsState: { allClinics }, getAllClinics } = useClinics();

  const [partner, setPartner] = React.useState({});
  const [partnerInfo, setPartnerInfo] = React.useState(null);
  const [edit, setEdit] = React.useState(false);

  const [showDeleteUserModal, setShowDeleteUserModal] = React.useState(false);

  useEffect(() => {
    (async function fetchDetails() {
      await getUserDetails(id).then(res => {
        setPartner(res?.data);
        getAllClinics();
        getPartnerInfo(id).then((res) => {
          setPartnerInfo(res?.data?.data);
          setIsLoading(false)
        })
      }).catch((err) => {
          history.push('/manage-partners')
      })
    })();
  }, [id])

  const [userEdits, _setUserEdits] = useState({
    partnerInfo: {}
  });

  const setUserEdits = (newVal) => _setUserEdits({ ...userEdits, ...newVal });

  
  const [data, setData] = React.useState([]);  


  const handleUpdateClick = () => {
    updateUserById(partner._id, userEdits).then(res => {
      setPartner(res.data);
    });
    setEdit(false);
  }

  React.useEffect(() => {
    const dataToPush = [];
    if (allClinics && partnerInfo?.clinics) {
      partnerInfo.clinics.forEach(partnerClinic => {
        const clinicMatch = allClinics.find(clinic => clinic._id === partnerClinic._id);
        if (clinicMatch) {
          dataToPush.push({
            name: clinicMatch.name,
            type: clinicMatch.type,
            city: clinicMatch.location.city,
            _id: clinicMatch._id,
          })
        }
      })
    }
    setData(dataToPush);
  }, [partnerInfo])

  const handleUpdatePartnerInfoClick = () => {
    const associatedClinicIDs = data.map(clinic => clinic._id);
    updatePartnerInfo(partnerInfo._id, { type: userEdits.partnerInfo.type, clinics: associatedClinicIDs}).then(res => {
      setPartnerInfo({...partnerInfo, type: userEdits.partnerInfo.type, clinics: data})
      setEdit(false);
    })
  }

  const handleEditClick = (field) => {
    switch (field) {
      case "personalInfo":
        _setUserEdits({
          first_name: partner.first_name,
          last_name: partner.last_name,
        });
      break;
      case 'contactInfo':
        _setUserEdits({
          location: {
            address: partner.location.address,
            postal_code: partner.location.postal_code,
            city: partner.location.city,
            country: partner.location.country,
          },
          mobile_nb: partner.mobile_nb
        })
      break;
      case 'partnerInfo':
        _setUserEdits({
          partnerInfo: {
            type: partnerInfo.type
          }
        })
        break;
      default:
    }
    if (edit === field) setEdit(false)
    if (edit !== field) setEdit(field)
  }

  const handleDelete = () => {
    if (partner.active) {
      deleteUser(partner._id).then(() => history.push('/manage-partners'));
    }
    if (!partner.active) {
      forceDeleteUser(partner._id).then(() => history.push('/manage-partners'));
    }
  }
  
  const handleActivate = () => {
    activateUser(partner._id).then(() => history.push('/manage-partners'));
  }

  const countryOptions = [];
  if (!countryOptions.length) COUNTRIES.forEach(country => {
    countryOptions.push({
      value: country,
      label: country
    })
  });
  
  const partnerTypeOptions = [];
  if (!partnerTypeOptions.length) PARTNER_TYPES.map(type => (
    partnerTypeOptions.push({
      label: type,
      value: type
    })
  ));

  if (isLoading) return (
    <div className='flex justify-around items-center h-full'>
      <MoonLoader color={COLORS.colorPrimary}/>
    </div>
  )

  return (
    <div className="flex flex-col">
      <DeleteUserModal showDeleteUserModal={showDeleteUserModal} setShowDeleteUserModal={setShowDeleteUserModal} onDelete={handleDelete} forceDelete={partner?.active? false : true}/>
      <div className="bg-white rounded shadow py-12 px-10">
        <div className='flex justify-between items-center mb-10'>
          <h1 className="text-2xl text-secondary">Personal information</h1>
          <div className={`${edit === 'personalInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'personalInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('personalInfo')} className='cursor-pointer'/>
          </div>
        </div>
        <div className='grid grid-cols-1 gap-y-4'>  
          <FieldWithEdit label='First name' data={edit === 'personalInfo' ? userEdits.first_name : partner.first_name} onChange={(e) => setUserEdits({first_name: e.target.value})} edit={edit === 'personalInfo'}/>
          <FieldWithEdit label='Last name' data={edit === 'personalInfo' ? userEdits.last_name : partner.last_name} onChange={(e) => setUserEdits({last_name: e.target.value})} edit={edit === 'personalInfo'}/>
          <FieldWithEdit locked label='Email' data={partner.email} edit={edit === 'personalInfo'}/>
          <FieldWithEdit 
            type='date' 
            label='Date of Birth' 
            locked
            edit={edit === 'personalInfo'}
            data={partner?.date_of_birth ? dayjs(partner.date_of_birth).format('DD/MM/YYYY') : 'n/a'} 
          />
          <FieldWithEdit override label='Profile picture'>
            <div className="flex items-center mt-1 sm:mt-0 justify-between w-full">
              <ProfilePicture user={partner}/>
              {edit === 'personalInfo' && 
                <ClosedLockIcon fill={COLORS.colorSecondary} className='mr-3'/>
              }
            </div>

          </FieldWithEdit>
          <FieldWithEdit locked data={partner.verified ? 'Account verified' : 'Account not verified'} label={'Verified'} edit={edit === 'personalInfo'}/>
          {edit === 'personalInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Save' onClick={handleUpdateClick} />
            </div>
          </div>}
        </div>
      </div>
      <div className="bg-white rounded shadow py-12 px-10 mt-6">
        <div className='flex justify-between items-center mb-10'>
          <h1 className=" text-2xl text-secondary">Contact details</h1>
          <div className={`${edit === 'contactInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'contactInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('contactInfo')} className='cursor-pointer'/>
          </div>
        </div>
        <div className='grid grid-cols-1 gap-y-4'>  
          <FieldWithSelectEdit label='Country' options={countryOptions} data={edit === 'contactInfo' ? userEdits.location.country : partner.location.country} onChange={(e) => setUserEdits({location: {...userEdits.location, country: e.value}})} edit={edit === 'contactInfo'}/> 
          <FieldWithEdit label='City' data={edit === 'contactInfo' ? userEdits.location.city : partner.location.city} onChange={(e) => setUserEdits({location: { ...userEdits.location, city: e.target.value}})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Address' data={edit === 'contactInfo' ? userEdits.location.address : partner.location.address} onChange={(e) => setUserEdits({location : {...userEdits.location, address: e.target.value}})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Postal code' data={edit === 'contactInfo' ? userEdits.location.postal_code : partner.location.postal_code} onChange={(e) => setUserEdits({location: {...userEdits.location, postal_code: e.target.value}})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Mobile' data={edit === 'contactInfo' ? userEdits.mobile_nb : partner.mobile_nb} onChange={(e) => setUserEdits({mobile_nb: e.target.value})} edit={edit === 'contactInfo'}/>
          {edit === 'contactInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Save' onClick={handleUpdateClick} />
            </div>
          </div>}
        </div>
      </div>
      <div className="bg-white rounded shadow py-12 px-10 mt-6">
        <div className='flex justify-between items-center mb-10 '>
          <h1 className="text-2xl text-secondary">Partner information</h1>
          <div className={`${edit === 'partnerInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'partnerInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('partnerInfo')} className='cursor-pointer'/>
          </div>
        </div>
        <div className='grid grid-cols-1 gap-y-4'>  
          <FieldWithSelectEdit label='Partner type' options={partnerTypeOptions} data={edit === 'partnerInfo' ? userEdits.partnerInfo.type : partnerInfo.type} onChange={(e) => setUserEdits({partnerInfo: {...userEdits.partnerInfo, type: e.value}})} edit={edit === 'partnerInfo'}/> 
          <FieldWithEdit label='Calendly' data={partner.calendly.organization_member ? "Yes" : "No"} locked edit={edit === 'partnerInfo'}/>
          <FieldWithEdit 
              override
              label='Clinic associations' 
              edit={edit === 'partnerInfo'}
            >
              {((edit === 'partnerInfo') || data.length) ? 
                <EditClinicAssociations allClinics={allClinics} partnerInfo={partnerInfo} edit={(edit === 'partnerInfo')} data={data} setData={setData}/> 
                : 
                <span>No associated clinics</span>
              }
          </FieldWithEdit>
          {edit === 'partnerInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Save' onClick={handleUpdatePartnerInfoClick} />
            </div>
          </div>}
        </div>
      </div>
      <div className='w-full flex flex-row-reverse pb-4'>
        <PrimaryButton className="w-32 mt-8" text='Back' onClick={() => history.push('/manage-partners')} />
        <RedButton className="w-32 mr-4 mt-8" text={partner?.active ? 'Delete user' : 'Erase user'} onClick={() => setShowDeleteUserModal(true)} />
        {!partner?.active && <GreenButton className="w-32 mr-4 mt-8" text={'Activate user'} onClick={handleActivate} />}
      </div>
    </div>
  )
}

export default ViewPartnerDetails
